import { resolveApiBaseUrl } from "./apiBase";

const API_URL = resolveApiBaseUrl();

const getAuthToken = (): string | null => {
  const token = localStorage.getItem('idToken') || localStorage.getItem('authToken');
  return token ? token.trim() : null;
};

const buildHeaders = () => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Authentication required');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`
  };
};

export type FormConfigField = {
  name: string;
  label?: string;
  description?: string;
  required?: boolean;
  hidden?: boolean;
  order?: number;
};

export type FollowUpRule = {
  triggerField: string;
  triggerValue: string;
  followUpFormUid: string;
  completionField?: string;
  completionValue?: string;
  delivery?: {
    email?: boolean;
    inApp?: boolean;
  };
};

/** Stored on the form; resolved with legacy singleSubmissionPerEmail on the backend. */
export type FormSubmissionPolicy = 'open' | 'overwrite' | 'duplicate';

/** Public-facing lifecycle for the shared link (stored as status draft / archived / published; UI labels draft / closed / open). */
export type FormAvailability = 'draft' | 'closed' | 'open';

/** How returning submitters interact with prior rows (stored as submissionPolicy + singleSubmissionPerEmail). */
export type FormEditOption =
  /** submissionPolicy open: new submissions only, no overwrite / prefilled edits */
  | 'no_edit'
  /** submissionPolicy overwrite */
  | 'overwrite'
  /** submissionPolicy duplicate */
  | 'duplicate';

export type FormConfig = {
  uid?: string;
  componentUid: string;
  componentSlug: string;
  /** When several collections share the same slug, disambiguates the target for public (anonymous) resolution. */
  componentOrganization?: string;
  title: string;
  subtitle?: string;
  iconUrl?: string;
  description?: string;
  /** Plain heading above the closed message on the public URL (default: Form closed). */
  closedHeader?: string;
  /** Rich text shown on the public URL when availability is closed. */
  closedMessage?: string;
  successMessage?: string;
  nextButtonLabel?: string;
  status: 'draft' | 'published' | 'archived';
  publicToken?: string;
  requireLogin?: boolean;
  singleSubmissionPerEmail?: boolean;
  submissionPolicy?: FormSubmissionPolicy;
  /** Only valid with submissionPolicy overwrite or duplicate: re-run Code / auto fields on resubmit instead of keeping prior values. */
  regenerateAutoFieldsOnUpdate?: boolean;
  /** Only valid with submissionPolicy overwrite or duplicate: field names respondents may change on resubmit. Empty allows all. */
  editableFieldsOnUpdate?: string[];
  firstNameField?: string;
  lastNameField?: string;
  /** Show form title in logged-in user submission lists (default true). */
  userSubmissionShowFormTitle?: boolean;
  /** Show submission date in logged-in user submission lists (default true). */
  userSubmissionShowDate?: boolean;
  /** Collection field names visible in logged-in user submission lists. */
  userSubmissionDisplayFields?: string[];
  /** Primary line in lists: formTitle | submissionDate | field name. */
  userSubmissionHeader?: string;
  verificationTemplateId?: string;
  completionTemplateId?: string;
  fields: FormConfigField[];
  followUpRules?: FollowUpRule[];
};

export function formConfigToAvailability(config: Pick<FormConfig, 'status'>): FormAvailability {
  if (config.status === 'draft') return 'draft';
  if (config.status === 'archived') return 'closed';
  return 'open';
}

export function formConfigToEditOption(
  config: Pick<FormConfig, 'submissionPolicy' | 'singleSubmissionPerEmail'>
): FormEditOption {
  if (config.submissionPolicy === 'overwrite') return 'overwrite';
  if (config.submissionPolicy === 'duplicate') return 'duplicate';
  return 'no_edit';
}

export function availabilityToConfigPatch(availability: FormAvailability): Pick<FormConfig, 'status'> {
  switch (availability) {
    case 'draft':
      return { status: 'draft' };
    case 'closed':
      return { status: 'archived' };
    case 'open':
      return { status: 'published' };
  }
}

export function editOptionToConfigPatch(
  edit: FormEditOption
): Partial<
  Pick<
    FormConfig,
    'submissionPolicy' | 'singleSubmissionPerEmail' | 'regenerateAutoFieldsOnUpdate' | 'editableFieldsOnUpdate'
  >
> {
  switch (edit) {
    case 'no_edit':
      return {
        submissionPolicy: 'open',
        singleSubmissionPerEmail: false,
        regenerateAutoFieldsOnUpdate: false,
        editableFieldsOnUpdate: []
      };
    case 'overwrite':
      return {
        submissionPolicy: 'overwrite',
        singleSubmissionPerEmail: false
      };
    case 'duplicate':
      return {
        submissionPolicy: 'duplicate',
        singleSubmissionPerEmail: false
      };
  }
}

export const fetchFormConfigs = async (componentSlug?: string): Promise<FormConfig[]> => {
  const result = await fetchFormConfigsWithMeta(componentSlug);
  return result.data;
};

export type FormTargetCollection = {
  uid: string;
  name: string;
  slug: string;
  fields: FormConfigField[];
  organization?: string;
};

export const fetchFormTargetCollections = async (): Promise<FormTargetCollection[]> => {
  const url = API_URL ? `${API_URL}/forms/target-collections` : '/forms/target-collections';
  const response = await fetch(url, {
    headers: buildHeaders()
  });
  const responseText = await response.text();
  if (!response.ok) {
    throw new Error(responseText || 'Failed to load form target collections');
  }
  const data = JSON.parse(responseText);
  if (!data.success) {
    throw new Error(data.message || 'Failed to load form target collections');
  }
  return data.data || [];
};

export type FormConfigListMeta = {
  formConfigAccess?: boolean;
  createFormCollectionUids?: string[];
};

export const fetchFormConfigsWithMeta = async (
  componentSlug?: string
): Promise<{ data: FormConfig[]; meta?: FormConfigListMeta }> => {
  const query = componentSlug ? `?componentSlug=${encodeURIComponent(componentSlug)}` : '';
  const url = API_URL ? `${API_URL}/forms${query}` : `/forms${query}`;
  const response = await fetch(url, {
    headers: buildHeaders()
  });
  const responseText = await response.text();
  if (!response.ok) {
    throw new Error(responseText || 'Failed to load form configs');
  }
  const data = JSON.parse(responseText);
  if (!data.success) {
    throw new Error(data.message || 'Failed to load form configs');
  }
  return {
    data: data.data || [],
    meta: data.meta
  };
};

export const fetchFormConfig = async (uid: string): Promise<FormConfig> => {
  const url = API_URL ? `${API_URL}/forms/${uid}` : `/forms/${uid}`;
  const response = await fetch(url, {
    headers: buildHeaders()
  });
  const responseText = await response.text();
  if (!response.ok) {
    throw new Error(responseText || 'Failed to load form config');
  }
  const data = JSON.parse(responseText);
  if (!data.success) {
    throw new Error(data.message || 'Failed to load form config');
  }
  return data.data;
};

export const createFormConfig = async (payload: FormConfig): Promise<FormConfig> => {
  const url = API_URL ? `${API_URL}/forms` : `/forms`;
  const response = await fetch(url, {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify(payload)
  });
  const responseText = await response.text();
  if (!response.ok) {
    throw new Error(responseText || 'Failed to create form config');
  }
  const data = JSON.parse(responseText);
  if (!data.success) {
    throw new Error(data.message || 'Failed to create form config');
  }
  return data.data;
};

export const updateFormConfig = async (uid: string, payload: FormConfig): Promise<FormConfig> => {
  const url = API_URL ? `${API_URL}/forms/${uid}` : `/forms/${uid}`;
  const response = await fetch(url, {
    method: 'PUT',
    headers: buildHeaders(),
    body: JSON.stringify(payload)
  });
  const responseText = await response.text();
  if (!response.ok) {
    throw new Error(responseText || 'Failed to update form config');
  }
  const data = JSON.parse(responseText);
  if (!data.success) {
    throw new Error(data.message || 'Failed to update form config');
  }
  return data.data;
};

export const deleteFormConfig = async (uid: string): Promise<void> => {
  const url = API_URL ? `${API_URL}/forms/${uid}` : `/forms/${uid}`;
  const response = await fetch(url, {
    method: 'DELETE',
    headers: buildHeaders()
  });
  const responseText = await response.text();
  if (!response.ok) {
    throw new Error(responseText || 'Failed to delete form config');
  }
};
